import { PlayerRef } from '@remotion/player';
import { useCallback, useContext, useMemo, useState } from 'react';
import { PreviewSize } from 'remotion';
import { EditModeContext } from '../../edit-mode';
import { FEATURE_CANVAS_MARQUEE_SELECTION } from '../../flags';
import { MarqueeSelection } from '../../marquee-selection';
import { unselectItemForCrop } from '../../state/actions/item-cropping';
import { unselectItemForZoomRegion } from '../../state/actions/zoom-region';
import { setSelectedItems } from '../../state/actions/set-selected-items';
import { unselectItems } from '../../state/actions/unselect-items';
import { CanvasTransformation } from '../calculate-canvas-transformation';
import { isLeftClick } from '../is-left-click';
import { useCurrentStateAsRef, useWriteContext } from '../use-context';
import { isItemInMarquee } from './is-item-in-marquee';

export const useCanvasMarqueeSelection = ({
  playerRef,
  canvasTransformation,
  previewSize,
  canvasRef,
}: {
  playerRef: React.RefObject<PlayerRef | null>;
  canvasTransformation: CanvasTransformation;
  previewSize: PreviewSize;
  canvasRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const [rect, setRect] = useState<MarqueeSelection | null>(null);
  const { setState } = useWriteContext();
  const state = useCurrentStateAsRef();
  const { editMode } = useContext(EditModeContext);

  const canvasLeft = canvasTransformation.centerX - previewSize.translation.x;
  const canvasTop = canvasTransformation.centerY - previewSize.translation.y;

  const onPointerDown: React.MouseEventHandler<HTMLDivElement> = useCallback(
    (initialPointerEvent) => {
      if (!isLeftClick(initialPointerEvent)) {
        return;
      }

      if (
        editMode === 'draw-blur' ||
        editMode === 'draw-solid' ||
        editMode === 'hand'
      ) {
        return;
      }

      setState({
        update: (s) =>
          unselectItemForZoomRegion(unselectItemForCrop(unselectItems(s))),
        commitToUndoStack: false,
      });

      if (!FEATURE_CANVAS_MARQUEE_SELECTION) {
        return;
      }

      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasRect) {
        throw new Error('need canvasrect');
      }

      const startX = initialPointerEvent.clientX - canvasRect.left;
      const startY = initialPointerEvent.clientY - canvasRect.top;

      const onPointerMove = (e: PointerEvent) => {
        const endX = e.clientX - canvasRect.left;
        const endY = e.clientY - canvasRect.top;

        const topLeftPoint = {
          x: Math.min(startX, endX),
          y: Math.min(startY, endY),
        };

        const bottomRightPoint = {
          x: Math.max(startX, endX),
          y: Math.max(startY, endY),
        };

        const rectToSet: MarqueeSelection = {
          start: topLeftPoint,
          end: bottomRightPoint,
        };

        setRect(rectToSet);

        const currentTime = playerRef.current?.getCurrentFrame() ?? null;
        if (currentTime === null) {
          throw new Error('need current time');
        }

        const selectedIds: string[] = [];

        const { items, tracks } = state.current.undoableState;

        for (const track of tracks) {
          if (track.hidden) {
            continue;
          }
          for (const itemId of track.items) {
            const item = items[itemId];
            const itemStartFrame = item?.from ?? 0;
            const itemEndFrame = itemStartFrame + (item?.durationInFrames ?? 0);
            const isItemVisibleAtCurrentTime =
              itemStartFrame <= currentTime && currentTime < itemEndFrame;

            if (!isItemVisibleAtCurrentTime) {
              continue;
            }

            const itemX =
              canvasLeft + (item?.left ?? 0) * canvasTransformation.scale;
            const itemY =
              canvasTop + (item?.top ?? 0) * canvasTransformation.scale;
            const itemEndX =
              (item?.width ?? 0) * canvasTransformation.scale + itemX;
            const itemEndY =
              (item?.height ?? 0) * canvasTransformation.scale + itemY;

            if (
              isItemInMarquee({
                marquee: rectToSet,
                itemX,
                itemY,
                itemEndX,
                itemEndY,
              })
            ) {
              selectedIds.push(item?.id ?? '');
            }
          }
        }

        setState({
          update: (prev) => setSelectedItems(prev, selectedIds),
          commitToUndoStack: false,
        });
      };

      const onPointerUp = () => {
        cleanup();
      };

      const cleanup = () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        setRect(null);
      };

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    },
    [
      canvasLeft,
      canvasRef,
      canvasTop,
      canvasTransformation.scale,
      editMode,
      playerRef,
      setState,
      state,
    ],
  );

  return useMemo(
    () => ({
      rect,
      onPointerDown,
    }),
    [onPointerDown, rect],
  );
};
